/**
 * @kantera/ui - Design System Theme
 *
 * CSS Custom Property-backed theme. All color values resolve to `var(--k-...)`
 * strings. The actual hex values live in `apps/web/src/index.css` under
 * `:root[data-theme="dark"]` and `:root[data-theme="light"]`.
 *
 * Components use these tokens in inline CSSProperties. The browser resolves
 * the CSS variable at paint time, so theme switching is instant (no re-render).
 */
/**
 * Helper: returns `rgba(var(--k-<name>-rgb), <alpha>)`.
 * Use when a component needs a translucent version of a theme color.
 */
function rgbA(varName) {
    return (alpha) => `rgba(var(${varName}), ${alpha})`;
}
export const theme = {
    // Color Palette — all values are CSS variable references
    colors: {
        // Base backgrounds
        bg: {
            primary: 'var(--k-bg-base)',
            secondary: 'var(--k-bg-elevated)',
            tertiary: 'var(--k-bg-tertiary)',
            surface: 'var(--k-bg-surface)',
            deep: 'var(--k-bg-deep)',
            card: 'var(--k-bg-card)',
            elevated: 'var(--k-bg-elevated)',
            hover: 'var(--k-bg-hover)',
            input: 'var(--k-bg-input)',
        },
        // Brand — emerald green (primary CTAs, logo)
        brand: {
            primary: 'var(--k-brand)',
            hover: 'var(--k-brand-hover)',
            dark: 'var(--k-brand-dark)',
            glow: 'var(--k-brand-glow)',
            glowStrong: 'var(--k-brand-glow-strong)',
            rgb: 'var(--k-brand-rgb)',
            rgbA: rgbA('--k-brand-rgb'),
        },
        // UI accent — indigo (navigation, interaction)
        accent: {
            primary: 'var(--k-accent)',
            hover: 'var(--k-accent-hover)',
            dark: 'var(--k-accent-dark)',
            glow: 'var(--k-accent-glow)',
            glowStrong: 'var(--k-accent-glow-strong)',
            rgb: 'var(--k-accent-rgb)',
            rgbA: rgbA('--k-accent-rgb'),
        },
        // Semantic colors
        success: {
            primary: 'var(--k-success)',
            dark: 'var(--k-success-dark)',
            light: '#86efac', // decorative only — not theme-switched
            rgb: 'var(--k-success-rgb)',
            rgbA: rgbA('--k-success-rgb'),
        },
        danger: {
            primary: 'var(--k-danger)',
            dark: 'var(--k-danger-dark)',
            light: '#fca5a5',
            rgb: 'var(--k-danger-rgb)',
            rgbA: rgbA('--k-danger-rgb'),
        },
        warning: {
            primary: 'var(--k-warning)',
            dark: 'var(--k-warning-dark)',
            light: '#fcd34d',
            rgb: 'var(--k-warning-rgb)',
            rgbA: rgbA('--k-warning-rgb'),
        },
        info: {
            primary: 'var(--k-info)',
            dark: 'var(--k-info-dark)',
            light: '#67e8f9',
            rgb: 'var(--k-info-rgb)',
            rgbA: rgbA('--k-info-rgb'),
        },
        // Text colors
        text: {
            primary: 'var(--k-text-primary)',
            secondary: 'var(--k-text-secondary)',
            tertiary: 'var(--k-text-tertiary)',
            muted: 'var(--k-text-muted)',
            inverse: 'var(--k-text-inverse)',
            faint: 'var(--k-text-faint)',
        },
        // Button text — accessible contrast on bright backgrounds
        buttonText: {
            brand: 'var(--k-brand-text)',
            accent: 'var(--k-accent-text)',
            danger: 'var(--k-danger-text)',
        },
        // Neutral scale (static — used for non-themed decorative purposes)
        neutral: {
            50: '#f8fafc',
            100: '#f1f5f9',
            200: '#e2e8f0',
            300: '#cbd5e1',
            400: '#94a3b8',
            500: '#64748b',
            600: '#475569',
            700: '#334155',
            800: '#1e293b',
            900: '#0f172a',
        },
        // Borders
        border: {
            primary: 'var(--k-border-primary)',
            secondary: 'var(--k-border-secondary)',
            accent: 'var(--k-accent)',
            light: 'var(--k-border-light)',
            default: 'var(--k-border)',
            hover: 'var(--k-border-hover)',
            brand: 'var(--k-border-brand)',
        },
        // Overlays & glass
        overlay: {
            dark: 'var(--k-overlay)',
            darker: 'var(--k-overlay)',
            light: 'var(--k-overlay-light)',
        },
        glass: {
            default: 'var(--k-glass)',
            strong: 'var(--k-glass-strong)',
        },
    },
    // Spacing Scale (4px base)
    spacing: {
        xs: '4px',
        sm: '8px',
        md: '12px',
        lg: '16px',
        xl: '24px',
        '2xl': '32px',
        '3xl': '48px',
        '4xl': '64px',
    },
    // Typography
    typography: {
        fontFamily: {
            // Body text — Geist (mockup body face); falls back to Inter/system.
            primary: 'var(--k-font-body)',
            // Display headlines — Space Grotesk.
            display: 'var(--k-font-display)',
            // Mono labels / eyebrows — JetBrains Mono.
            mono: 'var(--k-font-mono)',
        },
        fontSize: {
            xs: {
                size: '12px',
                lineHeight: '16px',
                letterSpacing: '0.5px',
            },
            sm: {
                size: '14px',
                lineHeight: '20px',
                letterSpacing: '0px',
            },
            base: {
                size: '16px',
                lineHeight: '24px',
                letterSpacing: '0px',
            },
            lg: {
                size: '18px',
                lineHeight: '28px',
                letterSpacing: '0px',
            },
            xl: {
                size: '20px',
                lineHeight: '28px',
                letterSpacing: '-0.5px',
            },
            '2xl': {
                size: '24px',
                lineHeight: '32px',
                letterSpacing: '-0.5px',
            },
            '3xl': {
                size: '30px',
                lineHeight: '36px',
                letterSpacing: '-1px',
            },
        },
        fontWeight: {
            light: 300,
            normal: 400,
            medium: 500,
            semibold: 600,
            bold: 700,
            extrabold: 800,
        },
    },
    // Border Radius
    radius: {
        sm: '6px',
        md: '10px',
        lg: '14px',
        card: '18px',
        xl: '20px',
        full: '9999px',
    },
    // Shadows
    shadows: {
        none: 'none',
        sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        glow: '0 0 20px rgba(var(--k-accent-rgb), 0.3)',
        'glow-strong': '0 0 30px rgba(var(--k-accent-rgb), 0.5)',
        'glow-brand': '0 0 20px rgba(var(--k-brand-rgb), 0.3)',
        inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)',
        // Mockup surface depth ladder — theme-adaptive (dark = deep, light = soft warm)
        surface: 'var(--k-shadow-surface)',
        raised: 'var(--k-shadow-raised)',
        aurora: 'var(--k-shadow-aurora)',
    },
    // Transitions
    transitions: {
        fast: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
        base: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
        slow: 'all 350ms cubic-bezier(0.4, 0, 0.2, 1)',
        color: 'color 250ms cubic-bezier(0.4, 0, 0.2, 1)',
        transform: 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
    },
    // Z-Index Scale
    zIndex: {
        hide: -1,
        auto: 'auto',
        base: 0,
        dropdown: 1000,
        sticky: 1020,
        fixed: 1030,
        modal: 1050,
        popover: 1060,
        tooltip: 1070,
    },
    // Breakpoints
    breakpoints: {
        xs: '320px',
        sm: '640px',
        md: '768px',
        lg: '1024px',
        xl: '1280px',
        '2xl': '1536px',
    },
};
//# sourceMappingURL=theme.js.map